import React, { useEffect, useRef, useState } from "react";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { clearStateID } from "../state/stateSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import {
  GridCellProps,
  GridCustomCellProps,
  GridCustomHeaderCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  Grid as KendoGrid,
  GridColumn as Column,
} from "@progress/kendo-react-grid";
import {
  HeaderThElement,
  PagerTargetEvent,
} from "@progress/kendo-react-data-tools";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import { MdDelete } from "react-icons/md";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import ButtonCreate from "../../components/drawer/ButtonCreate";
import ButtonEdit from "../../components/drawer/ButtonEdit";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import { checkAcessRights, formatIndianNumber } from "../../_helper/helper";
import {
  deleteMembershipPlan,
  getAllMemberShipPlans,
  MembershipPlanActiveInactive,
} from "./services/membershipplan.services";
import { setMemberShipPlanID } from "./membershipplanSlice";

const MemberShipPlan: React.FC = () => {
  const dispatch = useAppDispatch();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);
  useEffect(() => {
    dispatch(getAllMemberShipPlans());
    return () => {
      dispatch(closeDialog());
    };
  }, []);
  return (
    <>
      <DeleteMemberShipPlanDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Membership Plan List
          </Typography.h4>
          {isAddAccess && (
            <ButtonCreate
              label="Create Membership Plan"
              drawerName="membershipplanForm"
              // drawerWidth={600}
            />
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <MemberShipPlanGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const MemberShipPlanGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const MemberShipPlanList = useAppSelector(
    (state) => state.membershipplan.MemberShipPlanList
  );
  const loading = useAppSelector((state) => state.membershipplan.loading);
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? MemberShipPlanList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  // if (loading) return <LoadingPanel gridRef={gridRef} />;

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteMemberShipPlan"));
      dispatch(setMemberShipPlanID(ID));
    };
    return (
      <td
        className="action-td"
        style={{
          ...props.style,
        }}
      >
        {!props.dataItem.IsUsed && isEditAccess && (
          <ButtonEdit
            ID={props.dataItem.ID}
            drawerName="membershipplanForm"
            // drawerWidth={600}
          />
        )}
        {!props.dataItem.IsUsed && isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
          >
            <MdDelete
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className="table-header"
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const MyActiveInActtiveCommandCell = (props: GridCellProps) => {
    const [IsActive, setIsActive] = useState<boolean>(props.dataItem.IsActive);

    const handleActiveInActive = () => {
      setIsActive(!IsActive);
      const data = {
        ID: props.dataItem.ID,
        IsActive: !IsActive,
      };
      dispatch(MembershipPlanActiveInactive(data)).then((response: any) => {
        if (response.payload?.data?.Status === 200) {
          dispatch(getAllMemberShipPlans());
        } else {
          dispatch(getAllMemberShipPlans());
        }
      });
    };

    return (
      <td style={{ textAlign: "center" }}>
        {!props.dataItem.IsUsed && (
          <Switch
            disabled={isEditAccess ? false : true}
            name="IsActive"
            checked={IsActive}
            onChange={handleActiveInActive}
            size="small"
          />
        )}
      </td>
    );
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      {MemberShipPlanList && MemberShipPlanList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          filterable={true}
          filter={filter}
          data={
            filter
              ? filterBy(MemberShipPlanList, filter).slice(
                  page.skip,
                  page.take + page.skip
                )
              : MemberShipPlanList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={MemberShipPlanList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
          className="responsive-table"
        >
          <Column
            field="PlanName"
            title="Plan Name"
            cell={(props: any) => (
              <td
                className={`${isEditAccess ? "hoverableName" : "normalName"}`}
              >
                {isEditAccess ? (
                  <ButtonEdit
                    ID={props.dataItem.ID}
                    drawerName="membershipplanForm"
                    label={props.dataItem?.PlanName}
                    // drawerWidth={600}
                  />
                ) : (
                  props.dataItem?.PlanName
                )}
              </td>
            )}
            width={170}
          />

          <Column
            field="BussinessValue"
            title="Bussiness Value"
            width={130}
            cells={{
              data: (props) => (
                <td className="text-end fw-bold">
                  ₹ {formatIndianNumber(props.dataItem.BussinessValue)}
                </td>
              ),
            }}
          />
          <Column
            field="TicketCount"
            title="Ticket Count"
            width={110}
            cells={{
              data: (props) => (
                <td className="text-center fw-bold">
                  {props.dataItem.TicketCount}
                </td>
              ),
            }}
          />
          <Column
            field="DomesticFlightCharge"
            title="Dom. Charge"
            width={120}
            cells={{
              data: (props) => (
                <td className="text-end fw-bold">
                  ₹ {formatIndianNumber(props.dataItem.DomesticFlightCharge)}
                </td>
              ),
            }}
          />
          <Column
            field="InternationalFlightCharge"
            title="Int. Charge"
            width={120}
            cells={{
              data: (props) => (
                <td className="text-end fw-bold">
                  ₹{" "}
                  {formatIndianNumber(props.dataItem.InternationalFlightCharge)}
                </td>
              ),
            }}
          />
          <Column
            field="ID"
            title="Status"
            cells={{ data: MyActiveInActtiveCommandCell }}
            width={"90px"}
            filterable={false}
            locked={true}
          />
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              cell={MyEditCommandCell}
              width={"110px"}
              filterable={false}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteMemberShipPlanDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const MemberShipPlanID = useAppSelector(
    (state) => state.membershipplan.MemberShipPlanID
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteMembershipPlan(ID));
      if (response.payload?.Data?.Status === 200) {
        dispatch(getAllMemberShipPlans());
        dispatch(closeDialog());
        dispatch(clearStateID());
      } else {
        dispatch(getAllMemberShipPlans());
        dispatch(closeDialog());
        dispatch(clearStateID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteMemberShipPlan" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Membership Plan?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Membership Plan?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(MemberShipPlanID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default MemberShipPlan;
